import cn from 'classnames';
import { addDays, format, parseISO } from 'date-fns';
import {
  MetricChange,
  MonthOverMonth,
  QueryChange,
  TrendWindows
} from '../../alexa-visibility-trend-utils';

const WINDOW_DAYS = 30;
const LABEL_FORMAT = 'MMM d';

const windowLabel = (endDate: string): string => {
  const end = parseISO(endDate);
  const currentStart = addDays(end, -(WINDOW_DAYS - 1));
  const previousEnd = addDays(currentStart, -1);
  const previousStart = addDays(currentStart, -WINDOW_DAYS);
  const range = (from: Date, to: Date): string =>
    `${format(from, LABEL_FORMAT)} – ${format(to, LABEL_FORMAT)}`;
  return `Last 30 days (${range(currentStart, end)}) vs the 30 days before (${range(previousStart, previousEnd)})`;
};

const formatPercent = (value: number | null): string =>
  value === null ? '—' : `${value.toFixed(1)}%`;
const formatPosition = (value: number | null): string => (value === null ? '—' : value.toFixed(1));

// "+4.2 pts" / "-0.3" with a sign, or a dash when there is nothing to compare.
const formatDelta = (delta: number | null, unit: string): string => {
  if (delta === null) return '—';
  const rounded = Math.round(delta * 10) / 10;
  if (rounded === 0) return 'no change';
  return `${rounded > 0 ? '+' : ''}${rounded.toFixed(1)}${unit}`;
};

interface MetricCardProps {
  label: string;
  change: MetricChange;
  format: (value: number | null) => string;
  unit: string;
}

const MetricCard = ({ label, change, format: formatValue, unit }: MetricCardProps) => {
  // A change too small to show at one decimal is "no change", with no arrow or color.
  const moved = change.delta !== null && Math.round(change.delta * 10) !== 0;
  const improved = moved ? change.improved : undefined;
  return (
    <div className='aars-recap-metric'>
      <div className='aars-recap-metric-label'>{label}</div>
      <div className='aars-recap-metric-value'>{formatValue(change.current)}</div>
      <div
        className={cn('aars-recap-metric-delta', {
          'is-good': improved === true,
          'is-bad': improved === false
        })}
      >
        {improved === true && '▲ '}
        {improved === false && '▼ '}
        {formatDelta(change.delta, unit)}
      </div>
      <div className='aars-recap-metric-previous'>was {formatValue(change.previous)}</div>
    </div>
  );
};

interface ChangeListProps {
  title: string;
  tone: 'good' | 'bad';
  changes: QueryChange[];
  emptyText: string;
}

const ChangeList = ({ title, tone, changes, emptyText }: ChangeListProps) => (
  <div className={cn('aars-chart-card aars-recap-list', `is-${tone}`)}>
    <div className='aars-chart-card-title'>{title}</div>
    {changes.length === 0 ? (
      <div className='aars-trend-empty'>{emptyText}</div>
    ) : (
      <ol className='aars-recap-changes'>
        {changes.map(change => (
          <li key={change.query} className='aars-recap-change'>
            <span className='aars-recap-change-query'>{change.query}</span>
            <span className='aars-recap-change-values'>
              {change.previous.toFixed(0)}% → {change.current.toFixed(0)}%
              <strong>{formatDelta(change.delta, ' pts')}</strong>
            </span>
          </li>
        ))}
      </ol>
    )}
  </div>
);

interface TrendRecapProps {
  targetBrand: string;
  windows: TrendWindows;
  monthOverMonth: MonthOverMonth;
  improvements: QueryChange[];
  declines: QueryChange[];
}

// How the selected brand moved in the newest 30 days against the 30 before, and the queries that
// gained and lost the most visibility.
export const TrendRecap = ({
  targetBrand,
  windows,
  monthOverMonth,
  improvements,
  declines
}: TrendRecapProps) => (
  <div className='aars-recap'>
    <div className='aars-chart-card'>
      <div className='aars-chart-card-header'>
        <div className='aars-chart-card-title'>
          Recap: {targetBrand} month over month
          {windows.endDate && windows.previous.length > 0 && (
            <span className='aars-chart-card-subtitle'>{windowLabel(windows.endDate)}</span>
          )}
        </div>
      </div>
      {windows.previous.length === 0 ? (
        <div className='aars-trend-empty'>
          Not enough history yet: a month-over-month comparison needs results from the 30 days
          before the newest run.
        </div>
      ) : (
        <div className='aars-recap-metrics'>
          <MetricCard
            label='Visibility score'
            change={monthOverMonth.visibilityScore}
            format={formatPercent}
            unit=' pts'
          />
          <MetricCard
            label='SOAR (top-3 slot share)'
            change={monthOverMonth.soar}
            format={formatPercent}
            unit=' pts'
          />
          <MetricCard
            label='Share of voice'
            change={monthOverMonth.shareOfVoice}
            format={formatPercent}
            unit=' pts'
          />
          <MetricCard
            label='Avg position'
            change={monthOverMonth.avgPosition}
            format={formatPosition}
            unit=''
          />
        </div>
      )}
    </div>
    {windows.previous.length > 0 && (
      <div className='aars-recap-lists'>
        <ChangeList
          title='Top 5 improvements'
          tone='good'
          changes={improvements}
          emptyText='No queries gained visibility.'
        />
        <ChangeList
          title='Top 5 declines'
          tone='bad'
          changes={declines}
          emptyText='No queries lost visibility.'
        />
      </div>
    )}
  </div>
);
